import { CompanyConfigurationHandler } from "./../../../../models/domains/pbm/companies/company-configuration-handler";
import { Component, OnInit } from "@angular/core";
import { UntypedFormControl, Validators, UntypedFormGroup, UntypedFormBuilder } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";

import { CompanyService } from "src/app/ui/services/pbm/company.service";
import { Company } from "src/app/ui/models/domains/pbm/companies/company";
import { CompanyContact } from "src/app/ui/models/domains/pbm/companies/company-contact";
import { MaskInput } from "src/app/ui/models/domains/masks/mask.model";
import { ModalService } from "src/app/ui/services/modal.service";
import { DeleteContactDialogComponent } from "../delete-contact-dialog/delete-contact-dialog.component";
import { LoadingService } from "src/app/ui/services/loading.service";
import { FeatureViewService } from "src/app/ui/services/feature-view.service";
import { FeatureViews } from "src/app/ui/models/domains/features/custom-feature-views";
import { PageViews } from "src/app/ui/models/domains/features/custom-page-views";
import { Toast } from "../../../toast/toast";
import { CompanyValidator } from "../validators/company-validator";
import { CompanyContactDialogComponent } from "../company-contact-dialog/company-contact-dialog.component";
import { CompanyHandler } from "src/app/ui/models/domains/pbm/companies/company-handler";
import { UserPermissions } from "./../../../../models/domains/pbm/user/user-permissions";
import { UserService } from "src/app/ui/services/user.service";
import { RoutePath } from "src/app/ui/models/domains/route-paths.model";
import { AppContext } from "src/app/ui/contexts/app-context";
import { CompanyConfiguration } from "src/app/ui/models/domains/pbm/companies/company-configuration";
import { CompanyRemainingLimit } from "src/app/ui/models/domains/pbm/companies/company-remaining-limit";
import { MatDialogRef, MatDialog } from "@angular/material/dialog";
import { formatApiError } from "src/app/ui/utils/error-formatter.util";

@Component({
  selector: "edit-company",
  templateUrl: "./edit-company.component.html",
  styleUrls: ["./edit-company.component.scss"],
})
export class EditCompanyComponent implements OnInit {
  telephoneMask = MaskInput.getTelephoneMask();
  mobileMask = MaskInput.getMobileMask();
  fiscalNumberMask = MaskInput.getFicalNumberMask();
  zipCodeMask = MaskInput.getZipCodeMask();
  federalRegistrationMask = MaskInput.getFederalRegistrationMask();
  socialNumberMask = MaskInput.getSocialNumberMask();
  companyHasLimit: boolean = false;
  transactionalLimit: Number;

  company: Company = new Company();
  companyRemainingLimit: CompanyRemainingLimit = new CompanyRemainingLimit();
  companyConfigurations: CompanyConfiguration = new CompanyConfiguration();

  companyContactDialogRef: MatDialogRef<CompanyContactDialogComponent>;
  deleteContactDialogRef: MatDialogRef<DeleteContactDialogComponent>;

  formCompanyRegistration: UntypedFormGroup;
  formCompanyConfiguration: UntypedFormGroup;
  fiscalNumber = this.activatedRoute.snapshot.params.fiscalNumber;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private loadingService: LoadingService,
    private featureViewService: FeatureViewService,
    private companyService: CompanyService,
    private activatedRoute: ActivatedRoute,
    private dialog: MatDialog,
    private modalService: ModalService,
    private toast: Toast,
    private context: AppContext,
    private userService: UserService,
    private router: Router,
  ) { }

  async ngOnInit() {
    this.featureViewService.activeFeatureView(FeatureViews.CompanyEditFeatureView, PageViews.CompanyPageView);
    this.formCompanyRegistration = this.formBuilder.group({
      companyKey: new UntypedFormControl(""),
      unit: new UntypedFormControl("", Validators.required),
      companyType: new UntypedFormControl(""),
      headOfficeFiscalNumber: new UntypedFormControl("", Validators.required),
      fiscalNumber: new UntypedFormControl("", Validators.required),
      stateRegistration: new UntypedFormControl(""),
      municipalRegistration: new UntypedFormControl(""),
      corporateName: new UntypedFormControl("", Validators.required),
      tradingName: new UntypedFormControl(""),
      street: new UntypedFormControl("", Validators.required),
      number: new UntypedFormControl("", Validators.required),
      complement: new UntypedFormControl(""),
      district: new UntypedFormControl("", Validators.required),
      city: new UntypedFormControl("", Validators.required),
      region: new UntypedFormControl("", Validators.required),
      zipCode: new UntypedFormControl("", Validators.required),
      employeeCount: new UntypedFormControl(0, [Validators.required]),
      isActive: new UntypedFormControl(false),
    });

    this.formCompanyConfiguration = this.formBuilder.group({
      transactionalLimit: new UntypedFormControl(""),
      currentLimit: new UntypedFormControl(""),
    });

    await this.searchCompany(this.fiscalNumber);
  }

  async searchCompany(fiscalNumber: string) {
    const ref = this.loadingService.beginLoading();
    const companyHandler = new CompanyHandler(this.formCompanyRegistration);
    const companyConfigurationHandler = new CompanyConfigurationHandler(this.formCompanyConfiguration);
    try {
      this.company = await this.companyService.findCompanyBy(fiscalNumber);
      if (this.company) {
        companyHandler.handleCompanyToForm(this.company);
        this.companyConfigurations = await this.getCompanyConfigurations();
        if (this.companyConfigurations) {
          companyConfigurationHandler.handleCompanyConfigurationToForm(this.companyConfigurations);
          this.companyHasLimit = this.companyConfigurations.isHasTransactionalLimit();
        }
        this.companyRemainingLimit = await this.companyService.getCompanyRemainingLimit(fiscalNumber);
      } else this.router.navigate([RoutePath.SearchCompany]);
    } catch (error) {
      const message = formatApiError(error, `Erro ao buscar empresa com CNPJ ${fiscalNumber}.`);
      this.toast.showErrorToast(message);
      this.router.navigate([RoutePath.SearchCompany]);
      this.userService.setUserPreferences("");
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  getCompanyContacts(): CompanyContact[] {
    return this.company.getCompanyContacts();
  }

  async getCompanyConfigurations(): Promise<CompanyConfiguration> {
    try {
      return await this.companyService.companyConfigurations(this.fiscalNumber);
    } catch (error) {
      const message = formatApiError(error, `Erro ao buscar configurações da empresa.`);
      this.toast.showErrorToast(message);
    }
  }

  openContactRegistration() {
    const newCompanyContact = new CompanyContact();
    newCompanyContact.setCompanyFiscalNumber(this.company.getFiscalNumber());
    this.companyContactDialogRef = this.dialog.open(CompanyContactDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      width: "700px",
      height: "auto",
      data: newCompanyContact,
    });
    this.companyContactDialogRef.componentInstance.saveContact.subscribe(async (data) => {
      this.company = await this.companyService.findCompanyBy(this.formCompanyRegistration.value.fiscalNumber.replace(/[./-]/g, ""));
      this.toast.showSuccessToast("Contato salvo com sucesso");
    });
  }

  openContactEdit(contact: CompanyContact): void {
    contact.setCompanyFiscalNumber(this.company.getFiscalNumber());
    this.companyContactDialogRef = this.dialog.open(CompanyContactDialogComponent, {
      disableClose: true,
      hasBackdrop: true,
      width: "700px",
      height: "auto",
      data: contact,
    });
    this.companyContactDialogRef.componentInstance.editContact.subscribe(async (data) => {
      this.company = await this.companyService.findCompanyBy(this.formCompanyRegistration.value.fiscalNumber.replace(/[./-]/g, ""));
      this.toast.showSuccessToast("Contato salvo com sucesso");
    });
  }

  openDeleteContactModal(contact: CompanyContact): void {
    this.deleteContactDialogRef = this.dialog.open(DeleteContactDialogComponent, {
      height: "auto",
      hasBackdrop: true,
      disableClose: true,
    });
    this.deleteContactDialogRef.componentInstance.delete.subscribe((data) => this.deleteContact(contact));
  }

  private async deleteContact(contact: CompanyContact) {
    const ref = this.loadingService.beginLoading();
    const companyContactKey = contact.getCompanyContactKey();
    try {
      await this.companyService.deleteCompanyContact(companyContactKey);
      const companyContactList = await this.companyService.findContactBy(this.formCompanyRegistration.value.fiscalNumber.replace(/[./-]/g, ""));
      this.company.setCompanyContacts(companyContactList);
      this.deleteContactDialogRef.close();
      this.toast.showSuccessToast("Contato deletado com sucesso");
    } catch (error) {
      const message = formatApiError(error, `Erro ao deletar contato.`);
      this.modalService.showErrorMessage(message);
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  async submitCompanyData() {
    const ref = this.loadingService.beginLoading();
    const companyValidator = new CompanyValidator();
    if (companyValidator.validate(this.formCompanyRegistration).hasErrorMessages()) {
      this.toast.showWarningToast(companyValidator.getValidationResult().getErrorMessage());
      this.loadingService.finishLoading(ref);
    } else {
      const companyHandler = new CompanyHandler(this.formCompanyRegistration);
      const company = companyHandler.handleFormToCompany();
      try {
        const companyKey = await this.companyService.addCompany(company);
        this.formCompanyRegistration.patchValue({ companyKey });
        this.company = await this.companyService.findCompanyBy(company.getFiscalNumber());
        this.toast.showSuccessToast("Empresa salva com sucesso");
      } catch (error) {        
        const message = formatApiError(error, `Erro ao salvar empresa.`);
        this.toast.showWarningToast(message);
      } finally {
        this.loadingService.finishLoading(ref);
      }
    }
  }

  async submitCompanyConfigurationData() {
    const ref = this.loadingService.beginLoading();
    if (this.companyHasLimit == false) {
      this.formCompanyConfiguration.value.transactionalLimit = 0;
    }

    try {
      if (this.formCompanyConfiguration.value.transactionalLimit > 50000.0) {
        this.toast.showWarningToast("Valor informado maior que R$ 50.000,00");
      } else if (this.companyHasLimit == true && this.formCompanyConfiguration.value.transactionalLimit < 1.0) {
        this.toast.showWarningToast("Valor do limite inválido");
      } else {
        const companyExistance = await this.hasCompanyConfigurations();
        if (companyExistance) {
          await this.companyService.updateCompanyConfigurations(this.fiscalNumber, this.formCompanyConfiguration.value.transactionalLimit);
        } else {
          await this.companyService.createCompanyConfigurations(this.fiscalNumber, this.formCompanyConfiguration.value.transactionalLimit);
        }
        this.formCompanyConfiguration.value.transactionalLimit;
        this.toast.showSuccessToast("Configuração salva com sucesso");
      }
    } catch (error) {
      const message = formatApiError(error, `Erro ao salvar configurações da empresa.`);
      this.toast.showWarningToast(message);
    } finally {
      this.loadingService.finishLoading(ref);
    }
  }

  async hasCompanyConfigurations() {
    try {
      const companyExistance = await this.companyService.companyConfigurations(this.fiscalNumber);
      if (companyExistance) {
        return true;
      }
      return false;
    } catch {
      return false;
    }
  }

  useDataQuality(): boolean {
    return this.context.getCurrentTenant().useDataQuality;
  }

  hasAccessToEditCompany(): boolean {
    return this.userService.getLoggedUser().hasPermission(UserPermissions.EditCompany);
  }

  public backToSearchCompanies(): void {
    this.router.navigate([RoutePath.SearchCompany]);
  }
}
